import React from 'react';
import { Card, Badge, Flex, Tooltip, Tag, ConfigProvider } from '@app/ui';
import Icon from '@ant-design/icons';
import VideocameraOutlined from './icons/VideocameraOutlined';
import PhotoOutlined from './icons/PhotoOutlined';
import CarouselOutlined from './icons/CarouselOutlined';
import LinkOutlined from './icons/LinkOutlined';
import { shortDate } from '@app/utils/formatters';
import shortCurrency from '@app/utils/shortCurrency';
import { getCustomIcon } from '@app/utils/getSocialIcons';

type SocialPostCardProps = {
    card: React.ReactNode;
    width?: number | string;
    height?: number | string;
    socMediaHandle?: string;
    isVideo: boolean;
    isCarousel?: boolean;
    formattedPostUrl: string;
    createdAt: string | number | Date;
    sponsorMediaValue?: number;
    currency?: string;
    lowerSocName: string;
    sourceType: string;
    collectionNames?: Array<string | undefined>;
    isTextPost?: boolean;
    content?: string;
};

const getMediaIcon = (isCarousel: boolean, isVideo: boolean) => {
    if (isCarousel) {
        return { label: 'Carousel', component: CarouselOutlined };
    }

    return isVideo
        ? { label: 'Video', component: VideocameraOutlined }
        : { label: 'Image', component: PhotoOutlined };
};

const SocialPostCard: React.FC<SocialPostCardProps> = ({
    card,
    width,
    height,
    socMediaHandle,
    isVideo,
    isCarousel = false,
    formattedPostUrl,
    createdAt,
    sponsorMediaValue,
    currency,
    lowerSocName,
    sourceType,
    collectionNames,
    isTextPost,
    content,
}) => {
    // wrap card prop  with Badge.ribbon if socMediaHandle is provided
    // depending on the content, we need to adjust the height of the ribbon
    const contentLength = content?.length ?? 0;
    const contentHeight =
        isTextPost && contentLength < 120 ? 110 : contentLength;

    const ribbonHeight = isTextPost
        ? contentHeight
        : typeof height === 'number'
          ? height - 10
          : undefined;

    const mediaIcon = getMediaIcon(isCarousel, isVideo);

    const wrappedCard = socMediaHandle ? (
        <Badge.Ribbon
            text={
                socMediaHandle.length > 22
                    ? `${socMediaHandle.slice(0, 19)}...`
                    : socMediaHandle
            }
            style={{
                top: ribbonHeight,
            }}
        >
            {card}
        </Badge.Ribbon>
    ) : (
        card
    );

    return (
        <Card cover={wrappedCard} hoverable style={{ width }}>
            <Flex vertical gap={6}>
                <Flex gap={8} justify={'space-around'}>
                    <Tooltip title={sourceType}>
                        {getCustomIcon(lowerSocName, false)}
                    </Tooltip>
                    <Tooltip title={mediaIcon.label}>
                        <Icon
                            aria-label={mediaIcon.label}
                            className="media-icon"
                            component={mediaIcon.component}
                        />
                    </Tooltip>
                    <span className="smv-text">
                        <span className="smv-bold">SMV: </span>
                        {sponsorMediaValue !== undefined
                            ? shortCurrency(sponsorMediaValue, 2, currency)
                            : '-'}
                    </span>
                </Flex>
                <Flex justify={'space-between'}>
                    <span className="date-text"> {shortDate(createdAt)}</span>
                    <Tooltip title="Go to post">
                        <a
                            href={formattedPostUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="-block"
                            data-test="post-link"
                        >
                            <Icon component={LinkOutlined} />
                        </a>
                    </Tooltip>
                </Flex>
            </Flex>
            {collectionNames?.length > 0 && (
                <div
                    style={{
                        marginTop: 6,
                        paddingInline: 4,
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                    }}
                >
                    <ConfigProvider
                        theme={{
                            token: {
                                colorBorder: '#009FDF',
                            },
                        }}
                    >
                        <Tooltip title={collectionNames.join(', ')}>
                            <Tag className="tag-v2-modal ellipsis-tag">
                                <span className="ellipsis-text">
                                    {collectionNames.length === 1
                                        ? collectionNames[0]
                                        : `${collectionNames.length} Collections`}
                                </span>
                            </Tag>
                        </Tooltip>
                    </ConfigProvider>
                </div>
            )}
        </Card>
    );
};

export default SocialPostCard;
